import React, { useState, useCallback } from "react";
import { COLORS } from "../../../../theme/colors";
import { MATERIALS } from "../../../../theme/materials";
import { mcpApi, openapiApi } from "../../../../api/client";

// agent-experience-redesign-v1.md §6.4 (C2) — inline credential CRUD
// drawer per integration row. Replaces the previous "configure →"
// deep-link to Admin → Credentials with a same-page form that writes
// directly to the integration row.
//
// Trust model (Q3 = relax to developers):
// - Developers + admins can WRITE credentials.
// - Plaintext REVEAL stays admin-only with explicit show/hide.
// - business_user role gets read-only mask view.
//
// Per integration kind:
//   mcp     → updates mcp_servers.auth_token via PUT /api/mcp/servers/:id
//   openapi → updates openapi_integrations.credential_ref +
//             tenant_settings field via PUT /api/openapi-integrations/:id
//
// Test connection:
//   mcp     → POST /api/mcp/servers/test (validates URL + token)
//   openapi → POST /api/openapi-integrations/:id/test-credential
//
// On Save: refetch /wiring (parent passes onSaved) so the health
// pill + has_credential flag update within ~1s.

export default function CredentialDrawer({ kind, row, canRevealPlaintext, onClose, onSaved }) {
  if (kind === "mcp") {
    return <McpCredentialForm row={row} canRevealPlaintext={canRevealPlaintext}
      onClose={onClose} onSaved={onSaved} />;
  }
  if (kind === "openapi") {
    return <OpenApiCredentialForm row={row} canRevealPlaintext={canRevealPlaintext}
      onClose={onClose} onSaved={onSaved} />;
  }
  return null;
}

// ── MCP: edit auth_token + url ──────────────────────────────
function McpCredentialForm({ row, canRevealPlaintext, onClose, onSaved }) {
  const [authToken, setAuthToken] = useState("");
  const [url, setUrl] = useState(row.url || "");
  const [show, setShow] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [testing, setTesting] = useState(false);
  const [error, setError] = useState(null);
  const [testResult, setTestResult] = useState(null);

  const save = useCallback(async (e) => {
    e?.preventDefault?.();
    setError(null);
    setSubmitting(true);
    try {
      const patch = {};
      // Only send the token if the user typed something — empty
      // means "leave existing token alone."
      if (authToken.trim()) patch.auth_token = authToken;
      if (url && url !== row.url) patch.url = url;
      if (Object.keys(patch).length === 0) {
        onClose && onClose();
        return;
      }
      await mcpApi.editServer(row.id, patch);
      onSaved && onSaved();
      onClose && onClose();
    } catch (e) {
      setError(e?.message || String(e));
    } finally {
      setSubmitting(false);
    }
  }, [authToken, url, row, onSaved, onClose]);

  const test = useCallback(async () => {
    setError(null);
    setTesting(true);
    setTestResult(null);
    try {
      const payload = {
        url: url || row.url,
        connection_type: row.connection_type,
        auth_token: authToken || undefined, // server uses stored token if omitted
      };
      const res = await mcpApi.testServer(payload);
      setTestResult(res?.data || res || { status: "ok" });
    } catch (e) {
      setTestResult({ status: "error", error: e?.message || String(e) });
    } finally {
      setTesting(false);
    }
  }, [url, authToken, row]);

  return (
    <FormShell title={`Credentials: ${row.name}`} subtitle={row.server_id} onClose={onClose}>
      <form onSubmit={save} style={{ display: "flex", flexDirection: "column", gap: 10 }}>
        <FieldRow label="URL">
          <input type="text" value={url} onChange={(e) => setUrl(e.target.value)}
            placeholder={row.url || "https://mcp.example.com"}
            style={fieldStyle} />
        </FieldRow>

        <FieldRow label="Auth token"
          hint={row.has_credential ? "•••••••• stored — leave blank to keep current" : "Bearer or API key"}>
          <div style={{ display: "flex", gap: 6 }}>
            <input type={show ? "text" : "password"}
              value={authToken} onChange={(e) => setAuthToken(e.target.value)}
              placeholder={row.has_credential ? "•••••••• (leave blank to keep)" : ""}
              style={{ ...fieldStyle, flex: 1 }} />
            {canRevealPlaintext && authToken && (
              <button type="button" onClick={() => setShow(s => !s)}
                style={iconBtn}>{show ? "Hide" : "Show"}</button>
            )}
          </div>
        </FieldRow>

        {testResult && (
          <TestResultBanner result={testResult} />
        )}

        {error && <ErrorBanner error={error} />}

        <FormButtons
          onCancel={onClose}
          onTest={test}
          testing={testing}
          submitting={submitting}
          submitLabel="Save credentials"
        />
      </form>
    </FormShell>
  );
}

// ── OpenAPI: edit credential_ref ────────────────────────────
function OpenApiCredentialForm({ row, canRevealPlaintext, onClose, onSaved }) {
  const [credentialRef, setCredentialRef] = useState(row.credential_ref || "");
  const [submitting, setSubmitting] = useState(false);
  const [testing, setTesting] = useState(false);
  const [error, setError] = useState(null);
  const [testResult, setTestResult] = useState(null);

  const save = useCallback(async (e) => {
    e?.preventDefault?.();
    setError(null);
    setSubmitting(true);
    try {
      await openapiApi.update(row.id, { credential_ref: credentialRef });
      onSaved && onSaved();
      onClose && onClose();
    } catch (e) {
      setError(e?.message || String(e));
    } finally {
      setSubmitting(false);
    }
  }, [credentialRef, row, onSaved, onClose]);

  const test = useCallback(async () => {
    setError(null);
    setTesting(true);
    setTestResult(null);
    try {
      const res = await openapiApi.testCredential(row.id);
      setTestResult(res?.data || res || { status: "ok" });
    } catch (e) {
      setTestResult({ status: "error", error: e?.message || String(e) });
    } finally {
      setTesting(false);
    }
  }, [row]);

  return (
    <FormShell title={`Credentials: ${row.name}`} subtitle={`${row.slug} · ${row.auth_method}`} onClose={onClose}>
      <form onSubmit={save} style={{ display: "flex", flexDirection: "column", gap: 10 }}>
        <FieldRow label="Credential field"
          hint="Tenant settings field name where the secret is stored (e.g. 'stripe_api_key'). The value itself is configured in Admin → API Keys.">
          <input type="text" value={credentialRef} onChange={(e) => setCredentialRef(e.target.value)}
            placeholder="stripe_api_key"
            style={fieldStyle} />
        </FieldRow>

        <div style={{
          padding: "10px 12px", borderRadius: 6, background: COLORS.surface,
          border: `1px solid ${COLORS.border}`, fontSize: 11, color: COLORS.textMuted, lineHeight: 1.5,
        }}>
          OpenAPI credentials live in <strong>tenants.settings_json</strong> per the existing tenant-scoped invariant.
          This drawer updates the <strong>credential_ref</strong> pointer; the actual secret value is set in Admin → API Keys.
          Use Test below to verify the binding round-trip.
        </div>

        {testResult && <TestResultBanner result={testResult} />}
        {error && <ErrorBanner error={error} />}

        <FormButtons
          onCancel={onClose}
          onTest={test}
          testing={testing}
          submitting={submitting}
          submitLabel="Save"
        />
      </form>
    </FormShell>
  );
}

// ── Shared bits ─────────────────────────────────────────────
function FormShell({ title, subtitle, onClose, children }) {
  return (
    <div onClick={onClose} style={{
      position: "fixed", inset: 0, background: "rgba(0,0,0,0.55)",
      display: "flex", alignItems: "center", justifyContent: "center", zIndex: 200,
    }}>
      <div onClick={(e) => e.stopPropagation()} style={{
        background: COLORS.surface, border: `1px solid ${COLORS.border}`,
        borderRadius: 12, width: "90%", maxWidth: 500,
        boxShadow: MATERIALS?.spatialShadow ? MATERIALS.spatialShadow() : "0 16px 40px rgba(0,0,0,0.5)",
      }}>
        <div style={{ padding: "16px 20px", borderBottom: `1px solid ${COLORS.border}`, display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
          <div>
            <div style={{ fontSize: 14, fontWeight: 700, color: COLORS.text }}>{title}</div>
            {subtitle && (
              <div style={{ fontSize: 11, color: COLORS.textDim, marginTop: 2, fontFamily: "monospace" }}>{subtitle}</div>
            )}
          </div>
          <button type="button" onClick={onClose} style={{
            background: "transparent", border: "none", color: COLORS.textDim,
            cursor: "pointer", fontSize: 16, padding: 4,
          }}>✕</button>
        </div>
        <div style={{ padding: 20 }}>{children}</div>
      </div>
    </div>
  );
}

function FieldRow({ label, hint, children }) {
  return (
    <div>
      <label style={{
        display: "block", fontSize: 11, fontWeight: 600,
        color: COLORS.textMuted, marginBottom: 4, letterSpacing: "0.02em",
      }}>{label}</label>
      {children}
      {hint && <div style={{ fontSize: 10, color: COLORS.textDim, marginTop: 3 }}>{hint}</div>}
    </div>
  );
}

function FormButtons({ onCancel, onTest, testing, submitting, submitLabel = "Save" }) {
  return (
    <div style={{ display: "flex", gap: 8, justifyContent: "flex-end", marginTop: 8 }}>
      <button type="button" onClick={onCancel} style={{
        background: "transparent", border: `1px solid ${COLORS.border}`,
        color: COLORS.textDim, padding: "6px 14px", borderRadius: 6,
        fontSize: 12, cursor: "pointer",
      }}>Cancel</button>
      <button type="button" onClick={onTest} disabled={testing}
        style={{
          background: "transparent", border: `1px solid ${COLORS.cyan}66`,
          color: COLORS.cyan, padding: "6px 14px", borderRadius: 6,
          fontSize: 12, fontWeight: 600, cursor: testing ? "default" : "pointer",
          opacity: testing ? 0.6 : 1,
        }}>
        {testing ? "Testing…" : "Test connection"}
      </button>
      <button type="submit" disabled={submitting}
        style={{
          background: COLORS.accent, color: "#fff", border: "none",
          padding: "6px 14px", borderRadius: 6,
          fontSize: 12, fontWeight: 600, cursor: submitting ? "default" : "pointer",
          opacity: submitting ? 0.6 : 1,
        }}>
        {submitting ? "Saving…" : submitLabel}
      </button>
    </div>
  );
}

function TestResultBanner({ result }) {
  const ok = result?.status === "ok" || result?.success === true || result?.healthy === true;
  return (
    <div style={{
      padding: "8px 12px", borderRadius: 6,
      background: ok ? `${COLORS.green}15` : `${COLORS.pink || "#ef4444"}15`,
      border: `1px solid ${ok ? COLORS.green : COLORS.pink || "#ef4444"}55`,
      color: ok ? COLORS.green : COLORS.pink || "#ef4444",
      fontSize: 11, lineHeight: 1.5,
    }}>
      <strong>{ok ? "✓ Connected" : "✕ Failed"}</strong>
      {result.error && <span> — {result.error}</span>}
      {result.detail && <div style={{ marginTop: 4, fontFamily: "monospace", fontSize: 10 }}>{result.detail}</div>}
    </div>
  );
}

function ErrorBanner({ error }) {
  return (
    <div style={{
      padding: "8px 12px", borderRadius: 6,
      background: `${COLORS.pink || "#ef4444"}15`,
      border: `1px solid ${COLORS.pink || "#ef4444"}55`,
      color: COLORS.pink || "#ef4444", fontSize: 12,
    }}>{error}</div>
  );
}

const fieldStyle = {
  width: "100%", boxSizing: "border-box",
  padding: "8px 12px", fontSize: 12, color: COLORS.text,
  background: COLORS.surface,
  border: `1px solid ${COLORS.border}`, borderRadius: 6,
  outline: "none", fontFamily: "inherit",
};

const iconBtn = {
  padding: "8px 12px", fontSize: 11, color: COLORS.textMuted,
  background: "transparent", border: `1px solid ${COLORS.border}`,
  borderRadius: 6, cursor: "pointer",
};
